import { useQuery } from "@tanstack/react-query";
import { supabase } from "../supabase/supabase-client";
import { Tables } from "../types/supabase";

export type VoteAudit = Tables<"vote_audit">;

export interface VoteAuditWithEditor extends VoteAudit {
  editor?: Tables<"loge_members"> | null;
}

/**
 * Fetch the most recent vote change audit record for a specific member on a specific event.
 * Returns the record if the member has changed their vote to "maybe" (1) or "not going" (0)
 * after the event was confirmed.
 */
export const useVoteChangeAudit = (
  eventId?: number,
  dateOptionId?: number,
  memberId?: number
) => {
  return useQuery({
    queryKey: ["voteAudit", eventId, dateOptionId, memberId],
    enabled: !!eventId && !!dateOptionId && !!memberId,
    queryFn: async () => {
      if (!dateOptionId || !memberId) return null;

      const { data, error } = await supabase
        .from("vote_audit")
        .select("*")
        .eq("date_option_id", dateOptionId)
        .eq("user_id", memberId)
        .order("changed_at", { ascending: false })
        .limit(1)
        .single();

      if (error) {
        if (error.code === "PGRST116") return null; // No rows found
        throw error;
      }

      return data as VoteAudit | null;
    },
  });
};

/**
 * Fetch all vote changes for a specific event date option with editor info.
 */
export const useVoteChangeAuditsForOption = (dateOptionId?: number) => {
  return useQuery({
    queryKey: ["voteAudits", dateOptionId],
    enabled: !!dateOptionId,
    queryFn: async () => {
      if (!dateOptionId) return [];

      const { data, error } = await supabase
        .from("vote_audit")
        .select("*")
        .eq("date_option_id", dateOptionId)
        .order("changed_at", { ascending: false });

      if (error) throw error;

      // Fetch editor info for records where edited_by exists and differs from user_id
      const recordsWithEditors = await Promise.all(
        (data || []).map(async (audit) => {
          let editor = null;
          if (audit.edited_by && audit.edited_by !== audit.user_id) {
            const { data: editorData } = await supabase
              .from("loge_members")
              .select("*")
              .eq("id", audit.edited_by)
              .single();
            editor = editorData;
          }
          return { ...audit, editor } as VoteAuditWithEditor;
        })
      );

      return recordsWithEditors;
    },
  });
};
